"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Sun, CloudRain, Snowflake } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const seasonalityCycles = [{
  phase: "Peak Summer Surge",
  period: "May – June",
  icon: "Sun",
  focus: "Rate Maximization & Min-Stay Controls",
  summary: "High-volume leisure influx from Delhi-NCR and northern markets. Implement strict MLOS (Minimum Length of Stay) and dynamic yield fences to capture maximum ADR without premature sell-outs."
}, {
  phase: "Autumn & Winter Escapes",
  period: "Oct – Jan (Festivals & Snow)",
  icon: "Snowflake",
  focus: "Weekend Premium & Experience Packages",
  summary: "Strong weekend spike demand coupled with festive holidays and winter chill getaways. Drive direct booking packages, heritage experiences, and premium lake-view rate tiers."
}, {
  phase: "Monsoon & Shoulder Transitions",
  period: "July – September & Feb – April",
  icon: "CloudRain",
  focus: "Base Occupancy & Corporate/Workation Yield",
  summary: "Dampened transient volume. Deploy targeted corporate retreats, extended stay workations, and geo-fenced OTA promotions to sustain baseline RevPAR without brand-damaging rate slashes."
}];
const challenges = [{
  id: 1,
  title: "Hill Station Seasonality & Window Compression",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Manage sharp peak leisure surges against prolonged monsoon and shoulder dips. We build dynamic minimum length-of-stay controls and pace-based pricing so your Nainital property never relies on panic discounting.",
  metricLabel: "Strategy focus",
  metricValue: "Pace-based pacing & minimum stay restrictions"
}, {
  id: 2,
  title: "Aggressive Comp-Set Undercutting & Rate Wars",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Track intraday rate shifts across MakeMyTrip, Agoda, and Booking.com for Mall Road and hillside luxury clusters. Automated alerts protect your RevPAR while securing high-intent bookings ahead of market drops.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA monitoring & rule-based rate fencing"
}, {
  id: 3,
  title: "The OTA Commission Trap & Direct Weekend Yield",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Stop unauthorized B2B wholesaler leakage and reclaim margins lost to steep OTA commissions. We enforce strict rate parity across meta-search and drive direct guest conversions from high-spending NCR travelers.",
  metricLabel: "Strategy focus",
  metricValue: "Wholesaler rate fencing & direct channel boost"
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapenainital" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Nainital
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Nainital presents a dynamic hill station micro-market shaped by compressed booking windows, high-spending weekend traveler flows from Delhi-NCR, and acute seasonal shifts. Sunil Singh structures disciplined, data-backed revenue strategies built specifically for Nainital&apos;s heritage estates, lake-view hotels, and hillside boutique resorts.
          </p>
        </div>

        {/* Seasonality Strategy Block */}
        <div className="mb-16">
          <div className="mb-8 text-center max-w-2xl mx-auto">
            <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground">
              Nainital Seasonality & Demand Cycles
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground mt-2">
              A year-round yielding framework structured to maximize high-season ADR while insulating shoulder months.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {seasonalityCycles.map((season, i) => <div key={season.phase} data-index={i} className="rounded-lg border border-border bg-card p-6 flex flex-col justify-between shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="inline-flex size-10 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {season.icon === "Sun" && <Sun className="size-5 text-chart-2" />}
                      {season.icon === "Snowflake" && <Snowflake className="size-5 text-chart-2" />}
                      {season.icon === "CloudRain" && <CloudRain className="size-5 text-chart-2" />}
                    </div>
                    <Badge variant="secondary" className="text-xs font-mono">
                      {season.period}
                    </Badge>
                  </div>
                  <h4 className="font-serif text-lg font-bold text-foreground mb-1">
                    {season.phase}
                  </h4>
                  <p className="text-xs font-semibold uppercase tracking-wider text-secondary mb-3">
                    {season.focus}
                  </p>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {season.summary}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Nainital hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}